import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { useAuth } from '../lib/auth/AuthContext';
import { useMixpanel } from '../context/MixpanelContext';
import { SubscriptionStatus } from '../types';

const MixpanelTracker = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { isInitialized } = useMixpanel();
  const previousPath = useRef(location.pathname);
  const hasTrackedRevenue = useRef(false);
  const hasIdentifiedUser = useRef(false);

  // Effect for identifying user and setting user properties
  useEffect(() => {
    // Wait for Mixpanel to be initialized and user to be authenticated
    if (isInitialized && isAuthenticated && user && !hasIdentifiedUser.current) {
      mixpanel.identify(user.uid);
      mixpanel.people.set({
        $email: user.email,
        $name: user.displayName,
        userType: user.userType,
      });
      hasIdentifiedUser.current = true; // Mark as identified to prevent re-identifying
    }
  }, [isInitialized, isAuthenticated, user]);

  // Effect for tracking page views
  useEffect(() => {
    // Wait for Mixpanel to be initialized before tracking
    if (isInitialized && location.pathname !== previousPath.current) {
      previousPath.current = location.pathname;
      const pageName = location.pathname.split('/').filter(Boolean).join(' ') || 'Dashboard';
      mixpanel.track('Page View', {
        page: location.pathname,
        page_name: pageName,
      });
    }
  }, [isInitialized, location]);

  // Effect for tracking revenue
  useEffect(() => {
    // Ensure Mixpanel is ready, revenue hasn't been tracked, and subscription data exists
    if (!isInitialized || hasTrackedRevenue.current || !user?.subscription) {
      return;
    }

    const activeSub = user.subscription;

    // Proceed only if the subscription is active or trialing and has a plan
    if ((activeSub.status === SubscriptionStatus.ACTIVE || activeSub.status === SubscriptionStatus.TRIALING) && activeSub.plan) {
      let mrr = 0;
      let chargeAmount = 0;

      if (activeSub.plan.price_yearly && activeSub.plan.price_yearly > 0) {
        mrr = activeSub.plan.price_yearly / 12;
        chargeAmount = activeSub.plan.price_yearly;
      } else if (activeSub.plan.price_monthly && activeSub.plan.price_monthly > 0) {
        mrr = activeSub.plan.price_monthly;
        chargeAmount = activeSub.plan.price_monthly;
      }

      if (mrr > 0) {
        // Set MRR as a user property for revenue analytics
        mixpanel.people.set({
          '$mrr': parseFloat(mrr.toFixed(2)),
          'Plan Name': activeSub.plan.name,
          'Subscription Status': activeSub.status,
        });

        // Track a specific charge for individual transaction records
        mixpanel.people.track_charge(chargeAmount, {
          '$time': new Date(),
          'Plan': activeSub.plan.name,
        });

        hasTrackedRevenue.current = true; // Mark as tracked to prevent duplicates
      }
    }
  }, [isInitialized, user]); // Depend on initialization status and user object

  return null; // This component does not render anything.
};

export default MixpanelTracker;